import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import LigandTable from '../../components/LigandTable/LigandTable';
import MolstarViewer from '../../components/MolstarViewer/MolstarViewer';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import GraphlqlQuery from '../../components/GraphlqlQuery/GraphlqlQuery';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ListBox } from 'primereact/listbox';
import LigandNetwork from '../../components/LigandNetwork/LigandNetwork';
import { Dropdown } from 'primereact/dropdown';

import LigandAnnotationDonut from '../../components/LigandAnnotationDonut/LigandAnnotationDonut';
import TopLigandsChart from '../../components/TopLigandsChart/TopLigandsChart';
import AnchorResiduesChart from '../../components/AnchorResiduesChart/AnchorResiduesChart';
import LigandPocketAAChart from '../../components/LigandPocketAAChart/LigandPocketAAChart';
import LigandDistanceByAnnotation from '../../components/LigandDistanceByAnnotation/LigandDistanceByAnnotation';


const LigandSection = ({ type, target, ligandData, ligandLoadData, activeTabIndex, annotationMap }) => {

  const [visibleLigand, setVisibleLigand] = useState(false);
  const [positionLigand, setPositionLigand] = useState('center');
  const [structurePDBIdLigand, setStructurePDBIdLigand] = useState(undefined);
  const [positions, setPositions] = useState([]);
  const [chains, setChains] = useState([]);
  const [ligands, setLigands] = useState([]);
  const [annotation, setAnnotation] = useState();
  const [ligandsOption, setLigandsOption] = useState([]);
  const [selectedLigand, setSelectedLigand] = useState(null);
  const [superpose, setSuperpose] = useState(false);
  const [uniac, setUniac] = useState("");

   const ligandDataForPDB = useMemo(() => {
  if (!Array.isArray(ligandData) || !structurePDBIdLigand) return [];
  return ligandData.filter(
    (l) => l?.pdbId?.toUpperCase().trim() === structurePDBIdLigand.toUpperCase().trim()
  );
}, [ligandData, structurePDBIdLigand]);

    const STANDARD_AA = new Set([
      "ALA", "ARG", "ASN", "ASP", "CYS", "GLN", "GLU", "GLY", "HIS", "ILE",
      "LEU", "LYS", "MET", "PHE", "PRO", "SER", "THR", "TRP", "TYR", "VAL",
    ]);

  const showLigand = (position) => {
    setPositionLigand(position);
    setVisibleLigand(true);
  };

  useEffect(() => {
  // Only set the PDB ID if it's undefined and there is ligand data available
  if (structurePDBIdLigand === undefined && ligandData.length > 0) {
    setStructurePDBIdLigand(ligandData[0]?.pdbId);
    setUniac(ligandData[0]?.anPosition.split("_")[0]);
  }


   if (!Array.isArray(ligandData)) return;

  // self‑invoking async helper keeps the effect itself synchronous
  (async () => {
    const matchingLigands = ligandData.filter(
      ligand =>
        ligand?.pdbId?.toUpperCase().trim() === ligandData[0]?.pdbId.toUpperCase().trim()
    );

    if (matchingLigands.length === 0) {
      setPositions([]);
      setChains([]);
      setLigands([]);
      return;
    }



    const seen = new Set();
    const aaResidues = matchingLigands.flatMap(ligand => {
      const details = Array.isArray(ligand.details)
        ? ligand.details
        : ligand.details != null
          ? [ligand.details]
          : [];
      return details.flatMap(detail =>
        detail.split(" | ").map(endpoint => {
          const [chain, position, atom, residue] = endpoint.split("/");
          return { chain, position: Number(position), atom, residue };
        })
      );
    })
      .filter(({ residue }) => STANDARD_AA.has(residue))
      .filter(({ chain, position }) => {
        const key = `${chain}/${position}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const chains = aaResidues.map(r => r.chain);
      const positions = aaResidues.map(r => r.position);

      setPositions(positions);
      setChains(chains);

    const ligands = [...new Set(matchingLigands.map(ligand => ligand.ligand))];

    setLigands(ligands)

  })();
}, [ligandData, structurePDBIdLigand]);

  const uniqueLigandPdbIds = useMemo(() => {
    if (!Array.isArray(ligandData)) return [];
    const validPdbIds = ligandData
                          .map((ligand) => ligand?.pdbId)
                          .filter(pdbId => pdbId != null);
    return [...new Set(validPdbIds)];
  }, [ligandData]);

// Get bindingSite data byPDB to show in 3D structure
useEffect(() => {

  if (!structurePDBIdLigand || !Array.isArray(ligandData) || annotation !== 'binding') return;

  // self‑invoking async helper keeps the effect itself synchronous
  (async () => {
    const matchingLigands = ligandData.filter(c => c.pdbId?.toUpperCase() === structurePDBIdLigand);

    if (matchingLigands.length === 0) {
      setPositions([]);
      setChains([]);
      setLigands([]);
      return;
    }

    const seen = new Set();
    const aaResidues = matchingLigands.flatMap(ligand => {
      const details = Array.isArray(ligand.details)
        ? ligand.details
        : ligand.details != null
          ? [ligand.details]
          : [];
      return details.flatMap(detail =>
        detail.split(" | ").map(endpoint => {
          const [chain, position, atom, residue] = endpoint.split("/");
          return { chain, position: Number(position), atom, residue };
        })
      );
    })
      .filter(({ residue }) => STANDARD_AA.has(residue))
      .filter(({ chain, position }) => {
        const key = `${chain}/${position}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      const chains = aaResidues.map(r => r.chain);
      const positions = aaResidues.map(r => r.position);

      setPositions(positions);
      setChains(chains);
  })();
}, [structurePDBIdLigand, ligandData, annotation]);

const handlePDBLigand = (e) => {
    setStructurePDBIdLigand(e.target.value);
    setSuperpose(false);
    setSelectedLigand([]);
};

const handleReset = () => {
   setSuperpose(false);
}

const filterLigandsReset = () => {
    setSelectedLigand([]);
}

  return (
    <>
      <h2 className="display-6 fw-bold mb-5">
        Ligands
        <sup>
          <Button icon="pi pi-info" onClick={() => showLigand('top-right')} text size="small" className="btn-dialog" />
        </sup>
      </h2>

      <Dialog
        header="Ligands"
        visible={visibleLigand}
        position={positionLigand}
        style={{ width: '50vw' }}
        onHide={() => setVisibleLigand(false)}
        draggable={false}
        resizable={false}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
      >
        <p className="m-0 mb-2 text-justify">
          Ligand interactions across the PDBe archive, including detailed information on interacting macromolecule chains and the functional annotation of each ligand.
        </p>
        <ul>
                         <li className="text-justify">Position - Residue position in the protein sequence. </li>
                         <li className="text-justify">PDB ID - PDB ID of the entry</li>
                         <li className="text-justify">Ligand - Ligand HET code</li>
                         <li className="text-justify">inchikey - InChIKey of the bound ligand</li>
                         <li className="text-justify">Annotation - Functional categorisation of the bound ligand like cofactor-like/reactant-like/drug-like/ion</li>
                         <li className="text-justify">Distance - In house calculate distance. We considered residue-ligands contacts as those having the Cβ spatially closer than 8 Å (Cɑ for glycine) a distance cutoff commonly used.</li>
                         <li className="text-justify">Atoms: Residue atoms and ligand atoms in the binding site.</li>
                         <li className="text-justify">ChEMBL - Direct link to the ChEMBL website</li>
        </ul>
        <GraphlqlQuery type={type} target={target} tab={activeTabIndex} />
        <p className="m-0 mb-1 text-justify">
          For more information, please refer to the
          <Link className="" to="/about/" target="_blank"><b> about</b></Link> or
          <Link className="" to="/api/" target="_blank"><b> API</b></Link> page.
        </p>
      </Dialog>
      {ligandLoadData && (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
          <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="8" fill="var(--surface-ground)" animationDuration=".5s" />
        </div>
      )}
       {!ligandLoadData && (
        <div className="row mb-4">
           <LigandTable ligandData={ligandData} annotationMap={annotationMap}/>
        </div>
      )}
      {!ligandLoadData && ligandData.length > 0 && uniqueLigandPdbIds.length > 0 && (
             <div className="row mb-4">
               <h2 className="display-6 fw-bold mb-4">Structure</h2>
               <div className="col-md-2 mb-2">
                 <div className="bg-light rounded-3">
                   <div className="p-3">
                     <div className="mb-2">
                       <label htmlFor="structure-ligand" className="form-label">PDB ID</label>
                        <Dropdown
                                value={structurePDBIdLigand}
                                options={uniqueLigandPdbIds}
                                onChange={handlePDBLigand}
                                filter
                                placeholder="Select"
                                disabled={uniqueLigandPdbIds.length === 0}
                                style={{ width: '100%' }}
                                className="structure-dropdown mb-3"
                              />

                       <div className="d-flex">
                        <div className="burble-contact"></div>
                        <p className="fs--2 text-justify">Binding site</p>
                       </div>

                        {(ligandsOption.length > 0) && (
                             <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Ligand </label>
                               <ListBox multiple value={selectedLigand} onChange={(e) => setSelectedLigand(e.value)}
                                options={ligandsOption}
                                className="w-full md:w-14rem fs--2 text-justify list-filter mb-2" listStyle={{ maxHeight: '100px'}}/>

                                 <Button label="Unselect" icon="pi pi pi-refresh" className="fs--2 mb-1"
                                onClick={filterLigandsReset} />

                          </div>
                           )}
                           {( ligandsOption.length === 0) && (
                             <div className="mb-2">
                               <label htmlFor="filters" className="form-label">Ligand </label>
                               <p className="fs--2 text-justify"> No results found</p>
                             </div>
                            )}

                       <p className="fs--2 text-justify">
                         Click on the viewer plugin icons to access more advanced features from
                         <Link target="_blank" to="https://molstar.org/viewer-docs/"> Mol*</Link>
                       </p>
                     </div>
                   </div>
                 </div>
               </div>
               <div className="col-md-10">
                 <div className="viewer-box">
                     <MolstarViewer pdbId={structurePDBIdLigand} tab="ligands" database="PDB" selectedLigand={selectedLigand} superpose={superpose}
                     positions={positions} chain={chains} annotation={annotation} setLigandsOption={setLigandsOption}  uniac={uniac}/>
                 </div>
               </div>
{/* PER-STRUCTURE SUMMARY — driven by PDB ID dropdown */}
<h2 className="display-6 fw-bold mb-4 mt-4">
  Summary  |  {structurePDBIdLigand}
</h2>
<div className="row mb-4">
  <div className="col-md-4 mb-2">
    <LigandAnnotationDonut ligandData={ligandDataForPDB} />
  </div>
  <div className="col-md-4 mb-2">
    <LigandPocketAAChart ligandData={ligandDataForPDB} />
  </div>
  <div className="col-md-4 mb-2">
    <LigandDistanceByAnnotation ligandData={ligandDataForPDB} />
  </div>
</div>

{/* LIGAND–RESIDUE NETWORK — driven by PDB dropdown (ligandDataForPDB) */}
<div className="row mb-4">
  <LigandNetwork
    ligandData={ligandDataForPDB}
    id={`kd-bar-ligand-${structurePDBIdLigand}`}
  />
</div>

{/* OVERALL SUMMARY — aggregates the entire ligand library for this protein */}
<h2 className="display-6 fw-bold mb-2">Overall ligand profile</h2>
<p className="fs-1 text-justify mb-3">
  Aggregated view across all structures listed in the table above. Useful for
  identifying recurring ligands and anchor residues that define the binding
  pocket beyond a single experimental structure.
</p>
<div className="row mb-3">
  <div className="col-md-4">
    <LigandAnnotationDonut ligandData={ligandData} />
  </div>
  <div className="col-md-4">
    <TopLigandsChart ligandData={ligandData} />
  </div>
  <div className="col-md-4">
    <AnchorResiduesChart ligandData={ligandData} />
  </div>
</div>
<div className="row">
  <div className="col-md-4">
    <LigandPocketAAChart ligandData={ligandData} />
  </div>
  <div className="col-md-4">
    <LigandDistanceByAnnotation ligandData={ligandData} />
  </div>
</div>


             </div>

       )}
    </>
  );
};

export default LigandSection;
